import { Flex } from '@aws-amplify/ui-react';
import React, { useState } from 'react';
import { RxCornerBottomLeft } from 'react-icons/rx';
import { TbExternalLink } from 'react-icons/tb';
import { getCrmUrl } from '../utils/CrmUtils';
import { AssociationTypeSlideout } from './AssociationTypeSlideout';
import { EmailVerificationSlideout } from './EmailVerificationSlideout';
import './ExportReviewGrid.scss';
import { ExportReviewGridCell } from './ExportReviewGridCell';
import { DecisionReason, getReasonMessage } from './RecordExplanationSlideout';
import { SegmentField } from './SegmentField';
import UniversalTag from './UniversalTag';

// Types
export interface FieldMapping {
  source: string;
  target: string | null;
  label: string;
  visible: boolean;
  order: number;
}

export interface ReviewRecord {
  id_source: string;
  record_type: string;
  data: Record<string, unknown>;
  enrichment?: Record<string, unknown>;
  data_target?: Record<string, unknown>;
  system_data?: Record<string, unknown>;
  user_decision: string | null;
  operation?: string;
  id_crm?: string;
  email_status?: string;
  decision_reason?: DecisionReason;
  child_records?: ReviewChildRecord[];
}

export interface ReviewChildRecord {
  id_source: string;
  record_type: string;
  data: Record<string, unknown>;
  data_target?: Record<string, unknown>;
  user_decision: string | null;
  operation?: string;
  id_crm?: string;
  decision_reason?: DecisionReason;
}

export interface ExportReviewGridProps {
  records: ReviewRecord[];
  fieldMappings: FieldMapping[];
  systemFieldMappings?: FieldMapping[];
  hasEmailValidation: boolean;
  emailValidationProvider?: string;
  isCrmTarget: boolean;
  crmType?: 'HUBSPOT' | 'SALESFORCE';
  hubspotPortalId?: string;
  salesforceInstanceUrl?: string;
  entityType?: 'CONTACT' | 'INSTITUTION';
  onDecisionChange?: (idSource: string, decision: string) => void;
  onRecordClick?: (record: ReviewRecord | ReviewChildRecord, recordType: string, parentIdCrm?: string) => void;
  /** id_source of every marked record; marked rows stay highlighted so they are easy to track. */
  highlightedRecordIds?: Set<string>;
  /** Clicking anywhere in a row toggles its mark. Separate from onRecordClick, which is wired
   *  to individual tags and opens the explanation slideout. Tag clicks bubble to the row, so
   *  the row handler is the single place marking is decided. */
  onRowClick?: (idSource: string) => void;
  showAssociations?: boolean;
  readOnly?: boolean;
  showControls?: boolean;
  showChanges?: boolean;
}

// Helper to format provider name
const formatProviderName = (provider: string | undefined): string => {
  if (!provider) return '';
  const providerNames: Record<string, string> = {
    'ZEROBOUNCE': 'ZeroBounce',
    'NEVERBOUNCE': 'NeverBounce'
  };
  return providerNames[provider] || provider;
};

// Decision options with per-option colors
const decisionOptions = [
  { id: 'TAKE', label: 'Take', color: 'green' as const },
  { id: 'SKIP', label: 'Skip', color: 'yellow' as const }
];

// Matrix of operation + record_type -> UniversalTag category
const getOperationCategory = (operation: string, recordType: string): string => {
  const matrix: Record<string, Record<string, string>> = {
    'CREATE': {
      'CONTACT': 'CREATE_CONTACT',
      'INSTITUTION': 'CREATE',
      'CONTACT_INSTITUTION_ASSOCIATION': 'CREATE_ASSOCIATION'
    },
    'UPDATE': {
      'CONTACT': 'UPDATE_CONTACT',
      'INSTITUTION': 'UPDATE'
    },
    'REMOVE': {
      'CONTACT_INSTITUTION_ASSOCIATION': 'REMOVE_ASSOCIATION'
    }
  };
  return matrix[operation]?.[recordType] || operation;
};

// Configuration for child records that render in collapsed/summary format (single cell with name + type)
interface CollapsedChildConfig {
  getDisplayName: (data: Record<string, unknown>) => string;
  getDisplayType: (data: Record<string, unknown>) => string | undefined;
  getEduleadUrl: (data: Record<string, unknown>) => string | null;
  getIdCrm: (data: Record<string, unknown>) => string | undefined;
  ineligibleCategory: string;
}


/**
 * Tag for an association that will not be created, keyed on why.
 * A deliberate choice reads as "Not Linked"; a genuine blocker keeps the failure wording.
 */
function ineligibleAssociationTag(reasonCode: string | undefined, fallback: string): string {
  if (reasonCode === 'ALREADY_ASSOCIATED') return 'ALREADY_ASSOCIATED';
  if (reasonCode === 'SINGLE_ACCOUNT_ONLY') return 'NOT_LINKED';
  return fallback;
}

const collapsedChildConfigs: Record<string, CollapsedChildConfig> = {
  'CONTACT_INSTITUTION_ASSOCIATION': {
    getDisplayName: (data) => data.institution_name as string,
    getDisplayType: (data) => (data.institution_type as string)?.toLowerCase(),
    getEduleadUrl: (data) => {
      const id = data.id_institution as string;
      if (!id) return null;
      return `/directory/institutions/${id}`;
    },
    getIdCrm: (data) => data.id_crm_company as string,
    ineligibleCategory: 'INELIGIBLE_ASSOCIATION'
  }
};

// Record types that should render as full rows (with all columns) vs collapsed associations
const isFullRowRecordType = (recordType: string): boolean => {
  return recordType === 'INSTITUTION' || recordType === 'CONTACT';
};

export function ExportReviewGrid({
  records,
  fieldMappings,
  systemFieldMappings = [],
  hasEmailValidation,
  emailValidationProvider,
  isCrmTarget,
  crmType = 'HUBSPOT',
  hubspotPortalId,
  salesforceInstanceUrl,
  entityType = 'CONTACT',
  onDecisionChange,
  onRecordClick,
  highlightedRecordIds,
  onRowClick,
  showAssociations = true,
  readOnly = false,
  showControls = true,
  showChanges = true
}: ExportReviewGridProps) {
  const [emailSlideoutStatus, setEmailSlideoutStatus] = useState<string | null>(null);
  const [showAssociationTypeSlideout, setShowAssociationTypeSlideout] = useState(false);

  // Get display name for CRM
  const crmName = crmType === 'SALESFORCE' ? 'Salesforce' : 'HubSpot';
  const entityLabel = entityType === 'INSTITUTION' ? 'company' : 'contact';

  // Get visible field mappings sorted by order
  const visibleMappings = fieldMappings
    .filter(m => m.visible)
    .sort((a, b) => a.order - b.order);

  // Calculate sticky right positions
  const CELL_WIDTH_EMAIL = 100;
  const CELL_WIDTH_OPERATION = 140;
  const CELL_WIDTH_DECISION = 110;
  const CELL_WIDTH_LINKS = 55;

  const linksRight = 0;
  const decisionRight = CELL_WIDTH_LINKS;
  const operationRight = decisionRight + CELL_WIDTH_DECISION;
  const emailRight = isCrmTarget
    ? operationRight + CELL_WIDTH_OPERATION
    : decisionRight + CELL_WIDTH_DECISION;

  // Determine which column is the first sticky (leftmost) for shadow
  const firstStickyColumn = hasEmailValidation ? 'email' : isCrmTarget ? 'operation' : 'decision';

  // Render decision control
  // parentDecision is provided for child records to disable TAKE when parent is SKIP
  const renderDecisionControl = (
    record: ReviewRecord | ReviewChildRecord,
    recordType: string,
    parentDecision?: string | null
  ) => {
    const { id_source: idSource, user_decision: decision, decision_reason: reason } = record;

    if (!decision || decision === 'PENDING') {
      return <UniversalTag category="PENDING" />;
    }
    if (decision === 'INELIGIBLE') {
      const tooltipText = reason ? getReasonMessage(reason, crmName, entityType).short : undefined;
      return (
        <span
          data-tooltip-id={tooltipText ? 'main-tooltip' : undefined}
          data-tooltip-content={tooltipText}
        >
          <UniversalTag category={decision} onClick={onRecordClick ? () => onRecordClick(record, recordType) : undefined} />
        </span>
      );
    }
    // TAKE or SKIP - show as read-only tag or editable SegmentField
    // If parent is SKIP, child cannot be changed (locked to SKIP)
    const isLockedByParent = parentDecision === 'SKIP';
    if (readOnly) {
      return <UniversalTag category={decision} />;
    }
    return (
      <span
        data-tooltip-id={isLockedByParent ? 'main-tooltip' : undefined}
        data-tooltip-content={isLockedByParent ? 'Cannot export child when parent is skipped' : undefined}
      >
        <SegmentField
          mode="single"
          options={decisionOptions}
          selectedId={decision}
          onChange={(newDecision) => onDecisionChange?.(idSource, newDecision)}
          disabled={isLockedByParent}
        />
      </span>
    );
  };

  // Render operation tag
  const renderOperationTag = (record: ReviewRecord, recordType: string) => {
    const { operation, user_decision: decision, decision_reason: reason } = record;

    // Show N/A for ineligible records (clickable to show reason)
    if (decision === 'INELIGIBLE') {
      const tooltipText = reason ? getReasonMessage(reason, crmName, entityType).short : undefined;
      return (
        <span
          data-tooltip-id={tooltipText ? 'main-tooltip' : undefined}
          data-tooltip-content={tooltipText}
        >
          <UniversalTag category="NA" onClick={onRecordClick ? () => onRecordClick(record, recordType) : undefined} />
        </span>
      );
    }
    if (!operation) return null;

    const category = getOperationCategory(operation, recordType);
    const tooltipText = reason ? getReasonMessage(reason, crmName, entityType).short : undefined;

    return (
      <span
        data-tooltip-id={tooltipText ? 'main-tooltip' : undefined}
        data-tooltip-content={tooltipText}
      >
        <UniversalTag category={category} onClick={onRecordClick ? () => onRecordClick(record, recordType) : undefined} />
      </span>
    );
  };

  // Render links cell (Edulead link only, for CSV/JSON exports)
  const renderLinksCell = (idSource: string | null) => {
    const eduleadUrl = idSource ? `/directory/people/${idSource}` : null;

    return (
      <Flex direction="row" gap="6px" alignItems="center" justifyContent="flex-end" style={{ transform: 'translateY(3px)' }}>
        {eduleadUrl ? (
          <a href={eduleadUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}
            data-tooltip-id="main-tooltip" data-tooltip-content="View in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-60)' }} />
          </a>
        ) : (
          <span style={{ display: 'flex' }} data-tooltip-id="main-tooltip" data-tooltip-content="Not available in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-40)' }} />
          </span>
        )}
      </Flex>
    );
  };

  // Render links cell for CRM targets (includes both Edulead + CRM links)
  // For INSTITUTION exports, institutionType is needed to build the correct Edulead URL
  const renderCrmLinksCell = (idSource: string | null, idCrm?: string, institutionType?: string) => {
    // Build Edulead URL based on entity type
    let eduleadUrl: string | null = null;
    if (idSource) {
      if (entityType === 'INSTITUTION') {
        eduleadUrl = `/directory/institutions/${idSource}`;
      } else {
        eduleadUrl = `/directory/people/${idSource}`;
      }
    }

    // Build CRM URL based on entity type
    const crmEntityType = entityType === 'INSTITUTION' ? 'company' : 'contact';
    const crmUrl = getCrmUrl(crmType, crmEntityType, idCrm, { hubspotPortalId, salesforceInstanceUrl });

    return (
      <Flex direction="row" gap="6px" alignItems="center" justifyContent="flex-end" style={{ transform: 'translateY(3px)' }}>
        {eduleadUrl ? (
          <a href={eduleadUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}
            data-tooltip-id="main-tooltip" data-tooltip-content="View in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-60)' }} />
          </a>
        ) : (
          <span style={{ display: 'flex' }} data-tooltip-id="main-tooltip" data-tooltip-content="Not available in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-40)' }} />
          </span>
        )}
        {crmUrl ? (
          <a href={crmUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}
            data-tooltip-id="main-tooltip" data-tooltip-content={`View in ${crmName}`}>
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-60)' }} />
          </a>
        ) : (
          <span style={{ display: 'flex' }} data-tooltip-id="main-tooltip"
            data-tooltip-content={`This ${entityLabel} doesn't exist in ${crmName} yet`}>
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-40)' }} />
          </span>
        )}
      </Flex>
    );
  };

  // Render association links (for collapsed association rows)
  const renderAssociationLinks = (assoc: ReviewChildRecord) => {
    const config = collapsedChildConfigs[assoc.record_type];
    if (!config) return null;

    const assocData = assoc.data as Record<string, unknown>;
    const eduleadUrl = config.getEduleadUrl(assocData);
    const idCrm = config.getIdCrm(assocData);
    const crmUrl = getCrmUrl(crmType, 'company', idCrm, { hubspotPortalId, salesforceInstanceUrl });

    return (
      <Flex direction="row" gap="6px" alignItems="center" justifyContent="flex-end" style={{ transform: 'translateY(10px)' }}>
        {eduleadUrl ? (
          <a href={eduleadUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}
            data-tooltip-id="main-tooltip" data-tooltip-content="View in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-60)' }} />
          </a>
        ) : (
          <span style={{ display: 'flex' }} data-tooltip-id="main-tooltip" data-tooltip-content="Not in Edulead">
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-40)' }} />
          </span>
        )}
        {crmUrl ? (
          <a href={crmUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}
            data-tooltip-id="main-tooltip" data-tooltip-content={`View in ${crmName}`}>
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-60)' }} />
          </a>
        ) : (
          <span style={{ display: 'flex' }} data-tooltip-id="main-tooltip"
            data-tooltip-content={`Not in ${crmName} yet`}>
            <TbExternalLink style={{ color: 'var(--amplify-colors-neutral-40)' }} />
          </span>
        )}
      </Flex>
    );
  };

  // Render child record operation tag (associations or child institutions)
  const renderChildOperationTag = (child: ReviewChildRecord, parentIdCrm?: string) => {
    const { record_type: recordType, operation, user_decision: decision, decision_reason: reason } = child;

    // Show N/A for ineligible records (clickable to show reason)
    if (decision === 'INELIGIBLE') {
      const tooltipText = reason ? getReasonMessage(reason, crmName, entityType).short : undefined;
      return (
        <span
          data-tooltip-id={tooltipText ? 'main-tooltip' : undefined}
          data-tooltip-content={tooltipText}
        >
          <UniversalTag category="NA" onClick={onRecordClick ? () => onRecordClick(child, recordType, parentIdCrm) : undefined} />
        </span>
      );
    }
    if (!operation) return null;

    const category = getOperationCategory(operation, recordType);
    const tooltipText = reason ? getReasonMessage(reason, crmName, entityType).short : undefined;

    return (
      <span
        data-tooltip-id={tooltipText ? 'main-tooltip' : undefined}
        data-tooltip-content={tooltipText}
      >
        <UniversalTag category={category} onClick={onRecordClick ? () => onRecordClick(child, recordType, parentIdCrm) : undefined} />
      </span>
    );
  };

  return (
    <div className="export-review-grid">
        <table className="export-review-grid__table">
        <thead>
          <tr>
            {visibleMappings.map((mapping) => (
              <th key={mapping.source} className="export-review-grid__th mono-1">
                {mapping.target}
              </th>
            ))}
            {systemFieldMappings.map((mapping) => (
              <th key={`sys-${mapping.source}`} className="export-review-grid__th mono-1">
                {mapping.target}
              </th>
            ))}
            {showControls && hasEmailValidation && (
              <th
                className={`export-review-grid__th mono-1 export-review-grid__th--sticky${firstStickyColumn === 'email' ? ' export-review-grid__th--sticky-first' : ''}`}
                style={{ right: emailRight, width: CELL_WIDTH_EMAIL, minWidth: CELL_WIDTH_EMAIL, maxWidth: CELL_WIDTH_EMAIL }}
              >
                {formatProviderName(emailValidationProvider)}
              </th>
            )}
            {showControls && isCrmTarget && (
              <th
                className={`export-review-grid__th mono-1 export-review-grid__th--sticky${firstStickyColumn === 'operation' ? ' export-review-grid__th--sticky-first' : ''}`}
                style={{ right: operationRight, width: CELL_WIDTH_OPERATION, minWidth: CELL_WIDTH_OPERATION, maxWidth: CELL_WIDTH_OPERATION }}
              >
                Action
              </th>
            )}
            {showControls && (
              <th
                className={`export-review-grid__th mono-1 export-review-grid__th--sticky${firstStickyColumn === 'decision' ? ' export-review-grid__th--sticky-first' : ''}`}
                style={{ right: decisionRight, width: CELL_WIDTH_DECISION, minWidth: CELL_WIDTH_DECISION, maxWidth: CELL_WIDTH_DECISION }}
              >
                Decision
              </th>
            )}
            {showControls && (
              <th
                className="export-review-grid__th mono-1 export-review-grid__th--sticky"
                style={{ right: linksRight, width: CELL_WIDTH_LINKS, minWidth: CELL_WIDTH_LINKS, maxWidth: CELL_WIDTH_LINKS }}
              />
            )}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => (
            <React.Fragment key={record.id_source}>
              {/* Main record row */}
              <tr
                className={highlightedRecordIds?.has(record.id_source) ? 'export-review-grid__tr--selected' : undefined}
                onClick={onRowClick ? () => onRowClick(record.id_source) : undefined}
              >
                {visibleMappings.map((mapping) => {
                  // Check data first, then fall back to system_data for virtual fields like importDate
                  const value = record.data[mapping.source] ?? record.system_data?.[mapping.source];
                  const targetValue = record.data_target?.[mapping.source];
                  const changedFields = (record.decision_reason?.context?.changedFields as string[]) || [];
                  const isChanged = changedFields.includes(mapping.source);
                  const showCellHighlight = record.operation === 'UPDATE' && isChanged;
                  return (
                    <td key={mapping.source} className={`export-review-grid__td${showCellHighlight ? ' export-review-grid__td--changed' : ''}`}>
                      <ExportReviewGridCell
                        fieldName={mapping.target || mapping.source}
                        value={value}
                        targetValue={targetValue}
                        isChanged={isChanged}
                        showChanges={showChanges}
                        isUpdateOperation={record.operation === 'UPDATE'}
                      />
                    </td>
                  );
                })}
                {systemFieldMappings.map((mapping) => {
                  const value = record.system_data?.[mapping.source] as string;
                  return (
                    <td key={`sys-${mapping.source}`} className="export-review-grid__td">
                      {value || <UniversalTag category="Empty" />}
                    </td>
                  );
                })}
                {showControls && hasEmailValidation && (
                  <td
                    className={`export-review-grid__td export-review-grid__td--sticky${firstStickyColumn === 'email' ? ' export-review-grid__td--sticky-first' : ''}`}
                    style={{ right: emailRight, width: CELL_WIDTH_EMAIL, minWidth: CELL_WIDTH_EMAIL, maxWidth: CELL_WIDTH_EMAIL }}
                  >
                    {record.email_status ? <UniversalTag category={record.email_status} onClick={() => setEmailSlideoutStatus(record.email_status!)} /> : <UniversalTag category="NA" />}
                  </td>
                )}
                {/* For INELIGIBLE contacts with CRM target, merge API Call + Decision into one cell */}
                {showControls && isCrmTarget && record.user_decision === 'INELIGIBLE' ? (
                  <td
                    className={`export-review-grid__td export-review-grid__td--sticky${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                    style={{ right: decisionRight, width: CELL_WIDTH_OPERATION + CELL_WIDTH_DECISION }}
                    colSpan={2}
                  >
                    <UniversalTag
                      category={record.decision_reason?.code === 'ALREADY_ASSOCIATED' ? 'ALREADY_ASSOCIATED' : 'INELIGIBLE'}
                      onClick={onRecordClick ? () => onRecordClick(record, 'CONTACT') : undefined}
                    />
                    {record.decision_reason && record.decision_reason.code !== 'ALREADY_ASSOCIATED' && (
                      <span className="desc-15"> {getReasonMessage(record.decision_reason, crmName, entityType).short}</span>
                    )}
                  </td>
                ) : showControls ? (
                  <>
                    {isCrmTarget && (
                      <td
                        className={`export-review-grid__td export-review-grid__td--sticky${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                        style={{ right: operationRight, width: CELL_WIDTH_OPERATION, minWidth: CELL_WIDTH_OPERATION, maxWidth: CELL_WIDTH_OPERATION }}
                      >
                        {renderOperationTag(record, record.record_type)}
                      </td>
                    )}
                    <td
                      className={`export-review-grid__td export-review-grid__td--sticky${firstStickyColumn === 'decision' ? ' export-review-grid__td--sticky-first' : ''}`}
                      style={{ right: decisionRight, width: CELL_WIDTH_DECISION, minWidth: CELL_WIDTH_DECISION, maxWidth: CELL_WIDTH_DECISION }}
                    >
                      {renderDecisionControl(record, 'CONTACT')}
                    </td>
                  </>
                ) : null}
                {showControls && (
                  <td
                    className="export-review-grid__td export-review-grid__td--sticky"
                    style={{ right: linksRight, width: CELL_WIDTH_LINKS, minWidth: CELL_WIDTH_LINKS, maxWidth: CELL_WIDTH_LINKS }}
                  >
                    {isCrmTarget ? renderCrmLinksCell(record.id_source, record.id_crm, (record.enrichment?.type || record.data?.type) as string) : renderLinksCell(record.id_source)}
                  </td>
                )}
              </tr>

              {/* Child rows - render differently based on record type */}
              {showAssociations && record.child_records?.map((child) => {
                const childData = child.data as Record<string, unknown>;

                // Full-row child records (e.g., child institutions) - render like parent rows but nested
                if (isFullRowRecordType(child.record_type)) {
                  // Get institution type from data.type or decision_reason.context.institutionType
                  const institutionType = (childData.type as string) || (child.decision_reason?.context?.institutionType as string) || '';
                  return (
                    <tr
                      key={child.id_source}
                      className={`export-review-grid__tr--child${highlightedRecordIds?.has(child.id_source) ? ' export-review-grid__tr--selected' : ''}`}
                      onClick={onRowClick ? () => onRowClick(child.id_source) : undefined}
                    >
                      {visibleMappings.map((mapping, idx) => {
                        const value = childData[mapping.source];
                        const targetValue = child.data_target?.[mapping.source];
                        const changedFields = (child.decision_reason?.context?.changedFields as string[]) || [];
                        const isChanged = changedFields.includes(mapping.source);
                        const showCellHighlight = child.operation === 'UPDATE' && isChanged;
                        return (
                          <td key={mapping.source} className={`export-review-grid__td export-review-grid__td--child${showCellHighlight ? ' export-review-grid__td--changed' : ''}`}>
                            {idx === 0 && <span className="export-review-grid__child-connector"><RxCornerBottomLeft /></span>}
                            <ExportReviewGridCell
                              fieldName={mapping.target || mapping.source}
                              value={value}
                              targetValue={targetValue}
                              isChanged={isChanged}
                              showChanges={showChanges}
                              isUpdateOperation={child.operation === 'UPDATE'}
                            />
                          </td>
                        );
                      })}
                      {systemFieldMappings.map((mapping) => (
                        <td key={`sys-${mapping.source}`} className="export-review-grid__td export-review-grid__td--child" />
                      ))}
                      {showControls && hasEmailValidation && (
                        <td
                          className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--child${firstStickyColumn === 'email' ? ' export-review-grid__td--sticky-first' : ''}`}
                          style={{ right: emailRight, width: CELL_WIDTH_EMAIL, minWidth: CELL_WIDTH_EMAIL, maxWidth: CELL_WIDTH_EMAIL }}
                        />
                      )}
                      {showControls && isCrmTarget && child.user_decision === 'INELIGIBLE' ? (
                        <td
                          className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--child${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                          style={{ right: decisionRight, width: CELL_WIDTH_OPERATION + CELL_WIDTH_DECISION }}
                          colSpan={2}
                        >
                          <UniversalTag
                            category={ineligibleAssociationTag(child.decision_reason?.code, 'INELIGIBLE_ASSOCIATION')}
                            onClick={onRecordClick ? () => onRecordClick(child, child.record_type, record.id_crm) : undefined}
                          />
                        </td>
                      ) : showControls ? (
                        <>
                          {isCrmTarget && (
                            <td
                              className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--child${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                              style={{ right: operationRight, width: CELL_WIDTH_OPERATION, minWidth: CELL_WIDTH_OPERATION, maxWidth: CELL_WIDTH_OPERATION }}
                            >
                              {renderChildOperationTag(child, record.id_crm)}
                            </td>
                          )}
                          <td
                            className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--child${firstStickyColumn === 'decision' ? ' export-review-grid__td--sticky-first' : ''}`}
                            style={{ right: decisionRight, width: CELL_WIDTH_DECISION, minWidth: CELL_WIDTH_DECISION, maxWidth: CELL_WIDTH_DECISION }}
                          >
                            {renderDecisionControl(child, child.record_type, record.user_decision)}
                          </td>
                        </>
                      ) : null}
                      {showControls && (
                        <td
                          className="export-review-grid__td export-review-grid__td--sticky export-review-grid__td--child"
                          style={{ right: linksRight, width: CELL_WIDTH_LINKS, minWidth: CELL_WIDTH_LINKS, maxWidth: CELL_WIDTH_LINKS }}
                        >
                          {renderCrmLinksCell(child.id_source, child.id_crm as string, institutionType)}
                        </td>
                      )}
                    </tr>
                  );
                }

                // Collapsed association rows (e.g., contact-institution associations)
                const assocConfig = collapsedChildConfigs[child.record_type];
                if (!assocConfig) return null;

                const displayName = assocConfig.getDisplayName(childData);

                return (
                  <tr key={child.id_source} className="export-review-grid__tr--association">
                    <td className="export-review-grid__td export-review-grid__td--association" colSpan={visibleMappings.length + systemFieldMappings.length}>
                      <span className="export-review-grid__association-connector"><RxCornerBottomLeft /></span>
                      {/* Under single-account mode nothing indirect is created, so the
                          Indirect tag would contradict the Not Linked decision beside it. */}
                      {displayName} {crmType === 'SALESFORCE' && child.decision_reason?.code !== 'SINGLE_ACCOUNT_ONLY' && <UniversalTag category={childData.isPrimary ? 'PRIMARY' : 'INDIRECT'} onClick={() => setShowAssociationTypeSlideout(true)} />}
                    </td>
                    {showControls && hasEmailValidation && (
                      <td
                        className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--association-ctrl${firstStickyColumn === 'email' ? ' export-review-grid__td--sticky-first' : ''}`}
                        style={{ right: emailRight, width: CELL_WIDTH_EMAIL, minWidth: CELL_WIDTH_EMAIL, maxWidth: CELL_WIDTH_EMAIL }}
                      />
                    )}
                    {showControls && isCrmTarget && child.user_decision === 'INELIGIBLE' ? (
                      <td
                        className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--association-ctrl${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                        style={{ right: decisionRight, width: CELL_WIDTH_OPERATION + CELL_WIDTH_DECISION }}
                        colSpan={2}
                      >
                        <UniversalTag
                          category={ineligibleAssociationTag(child.decision_reason?.code, assocConfig.ineligibleCategory)}
                          onClick={onRecordClick ? () => onRecordClick(child, child.record_type, record.id_crm) : undefined}
                        />
                      </td>
                    ) : showControls ? (
                      <>
                        {isCrmTarget && (
                          <td
                            className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--association-ctrl${firstStickyColumn === 'operation' ? ' export-review-grid__td--sticky-first' : ''}`}
                            style={{ right: operationRight, width: CELL_WIDTH_OPERATION, minWidth: CELL_WIDTH_OPERATION, maxWidth: CELL_WIDTH_OPERATION }}
                          >
                            {renderChildOperationTag(child, record.id_crm)}
                          </td>
                        )}
                        <td
                          className={`export-review-grid__td export-review-grid__td--sticky export-review-grid__td--association-ctrl${firstStickyColumn === 'decision' ? ' export-review-grid__td--sticky-first' : ''}`}
                          style={{ right: decisionRight, width: CELL_WIDTH_DECISION, minWidth: CELL_WIDTH_DECISION, maxWidth: CELL_WIDTH_DECISION }}
                        >
                          {renderDecisionControl(child, child.record_type, record.user_decision)}
                        </td>
                      </>
                    ) : null}
                    {showControls && (
                      <td
                        className="export-review-grid__td export-review-grid__td--sticky export-review-grid__td--association-ctrl"
                        style={{ right: linksRight, width: CELL_WIDTH_LINKS, minWidth: CELL_WIDTH_LINKS, maxWidth: CELL_WIDTH_LINKS }}
                      >
                        {renderAssociationLinks(child)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <EmailVerificationSlideout
        isOpen={!!emailSlideoutStatus}
        onClose={() => setEmailSlideoutStatus(null)}
      />
      <AssociationTypeSlideout
        isOpen={showAssociationTypeSlideout}
        onClose={() => setShowAssociationTypeSlideout(false)}
      />
    </div>
  );
}
